<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { endOfDay, startOfDay, startOfMonth } from 'date-fns';
import ReportsAPI from 'dashboard/api/reports';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import Button from 'dashboard/components-next/button/Button.vue';

const copy = {
  title: 'Consumo de IA',
  subtitle:
    'Acompanhe tokens, chamadas e logs das conversas atendidas pela IA.',
  period: 'Período',
  filter: 'Filtrar',
  clear: 'Limpar',
  tokensUsed: 'Tokens usados',
  monthlyLimit: 'Limite mensal',
  openAiBalance: 'Saldo OpenAI',
  openAiSpent: 'consumidos de',
  openAiPermissionRequired:
    'A chave configurada não é uma Admin API Key válida ou não possui permissão para consultar o saldo. Configure uma Admin API Key da OpenAI.',
  openAiNotConfigured: 'Configure uma chave da OpenAI para consultar o saldo.',
  openAiUnavailable: 'Não foi possível consultar o saldo da OpenAI no momento.',
  openAiNoLimit: 'Nenhum limite mensal foi configurado na OpenAI.',
  calls: 'Chamadas IA',
  byFeature: 'Onde a IA foi utilizada',
  byFeatureDescription:
    'Veja quais atividades consumiram mais tokens no período.',
  logs: 'Logs das conversas da IA',
  empty: 'Nenhum consumo encontrado para este período.',
  columns: {
    time: 'Horário',
    conversation: 'Conversa',
    feature: 'Uso da IA',
    model: 'Modelo',
    tokens: 'Tokens utilizados',
    details: 'Detalhes',
  },
  seeLog: 'Ver log',
  previous: 'Anterior',
  next: 'Próxima',
  page: 'Página',
  of: 'de',
  hash: '#',
  emptyValue: '-',
  records: 'registro(s)',
  callsSuffix: 'chamada(s)',
  tokensSuffix: 'tokens',
  tokenInput: 'Entrada',
  tokenOutput: 'Saída',
  tokenTotal: 'Total',
};

const FEATURE_PRESENTATION = {
  agent_response: {
    label: 'Resposta automática ao cliente',
    description: 'Mensagem criada e enviada pela IA',
    icon: 'i-lucide-bot-message-square',
  },
  bank_result_customer_render: {
    label: 'Explicação de resultado bancário',
    description: 'Resultado bancário adaptado para o cliente',
    icon: 'i-lucide-landmark',
  },
  follow_up: {
    label: 'Continuação de uma solicitação',
    description: 'Nova orientação após uma resposta da IA',
    icon: 'i-lucide-message-circle-more',
  },
  image_analysis: {
    label: 'Análise de imagem',
    description: 'Leitura e interpretação de uma imagem',
    icon: 'i-lucide-image-search',
  },
  improve: {
    label: 'Melhoria de texto',
    description: 'Revisão e aprimoramento de uma mensagem',
    icon: 'i-lucide-wand-sparkles',
  },
  ixc_direct_response_action_audit: {
    label: 'Validação de resposta do IXC',
    description: 'Conferência automática antes do envio',
    icon: 'i-lucide-shield-check',
  },
  label_suggestion: {
    label: 'Sugestão de etiqueta',
    description: 'Indicação automática de etiquetas',
    icon: 'i-lucide-tags',
  },
  reply_suggestion: {
    label: 'Sugestão de resposta',
    description: 'Resposta sugerida para o atendente',
    icon: 'i-lucide-message-square-reply',
  },
  summarize: {
    label: 'Resumo da conversa',
    description: 'Resumo automático do atendimento',
    icon: 'i-lucide-file-text',
  },
  unknown: {
    label: 'Outro uso da IA',
    description: 'Consumo sem categoria identificada',
    icon: 'i-lucide-sparkles',
  },
};

const SOURCE_LABELS = {
  openai_node_responses_stream: 'Atendimento automático',
  openai_node_responses_bank_render: 'Resultado bancário',
  openai_node_responses_image: 'Análise de imagem',
  openai_node_responses: 'Automação da IA',
  'llm.follow_up': 'Continuação da solicitação',
  'llm.improve': 'Melhoria de texto',
  'llm.label_suggestion': 'Sugestão de etiqueta',
  'llm.reply_suggestion': 'Sugestão de resposta',
  'llm.summarize': 'Resumo da conversa',
};

const dateRange = ref([startOfMonth(new Date()), new Date()]);
const rangeType = ref(DATE_RANGE_TYPES.MONTH_TO_DATE);
const isLoading = ref(false);
const page = ref(1);
const perPage = 50;
const summary = ref({});
const byFeature = ref([]);
const events = ref([]);
const meta = ref({});
const openAiBilling = ref({});

const totalPages = computed(() => meta.value.total_pages || 0);
const totalEvents = computed(() => meta.value.total_count || 0);
const featureItems = computed(() =>
  [...byFeature.value].sort(
    (first, second) =>
      Number(second.total_tokens || 0) - Number(first.total_tokens || 0)
  )
);
const maxFeatureTokens = computed(() =>
  Math.max(...featureItems.value.map(item => Number(item.total_tokens || 0)), 1)
);
const monthlyLimit = computed(() => Number(summary.value.monthly_limit || 0));
const totalTokens = computed(() => Number(summary.value.total_tokens || 0));
const usagePercent = computed(() => {
  if (!monthlyLimit.value) return 0;

  return Math.min(
    100,
    Math.round((totalTokens.value / monthlyLimit.value) * 100)
  );
});

const formatNumber = value => Number(value || 0).toLocaleString('pt-BR');

const humanizeIdentifier = value =>
  String(value || 'unknown')
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const featurePresentation = featureName =>
  FEATURE_PRESENTATION[featureName || 'unknown'] || {
    label: humanizeIdentifier(featureName),
    description: 'Recurso de inteligência artificial',
    icon: 'i-lucide-sparkles',
  };

const sourceLabel = sourceType =>
  SOURCE_LABELS[sourceType] || humanizeIdentifier(sourceType);

const formatModel = model => {
  if (!model) return copy.emptyValue;

  return String(model)
    .replace(/-\d{4}-\d{2}-\d{2}$/, '')
    .replace(/^gpt-/i, 'GPT-')
    .replace(/-(mini|nano|pro)$/i, ' $1');
};

const featureUsagePercent = item =>
  Math.max(
    3,
    Math.round((Number(item.total_tokens || 0) / maxFeatureTokens.value) * 100)
  );

const formatCurrency = (value, currency = 'USD') =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency,
  });

const openAiBillingMessage = computed(() => {
  const messages = {
    permission_required: copy.openAiPermissionRequired,
    not_configured: copy.openAiNotConfigured,
    unavailable: copy.openAiUnavailable,
    no_limit: copy.openAiNoLimit,
  };

  return messages[openAiBilling.value.status] || copy.openAiUnavailable;
});

const formatDate = value => {
  if (!value) return '-';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatPeriod = () => {
  const [start, end] = dateRange.value;
  if (!start || !end) return '-';

  return `${new Date(start).toLocaleDateString('pt-BR')} - ${new Date(end).toLocaleDateString('pt-BR')}`;
};

const toEpoch = date => Math.floor(new Date(date).getTime() / 1000);

const loadReport = async () => {
  isLoading.value = true;
  try {
    const [start, end] = dateRange.value;
    const { data } = await ReportsAPI.getAiUsage({
      from: toEpoch(startOfDay(new Date(start))),
      to: toEpoch(endOfDay(new Date(end))),
      page: page.value,
      perPage,
    });
    summary.value = data.summary || {};
    openAiBilling.value = data.openai_billing || {};
    byFeature.value = data.by_feature || [];
    events.value = data.events || [];
    meta.value = data.meta || {};
  } catch {
    useAlert('Não foi possível carregar o relatório de consumo de IA.');
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChanged = ([start, end, type]) => {
  dateRange.value = [start, end];
  rangeType.value = type;
  page.value = 1;
  loadReport();
};

const applyFilters = () => {
  page.value = 1;
  loadReport();
};

const clearFilters = () => {
  dateRange.value = [startOfMonth(new Date()), new Date()];
  rangeType.value = DATE_RANGE_TYPES.MONTH_TO_DATE;
  page.value = 1;
  loadReport();
};

const conversationRoute = event => {
  const displayId =
    event.conversation?.display_id || event.conversation_display_id;

  if (!displayId) return null;

  return {
    name: 'inbox_conversation',
    params: { conversation_id: displayId },
  };
};

const metadataText = metadata => {
  if (!metadata || !Object.keys(metadata).length) return '';

  return JSON.stringify(metadata, null, 2);
};

watch(page, loadReport);
onMounted(loadReport);
</script>

<template>
  <div class="flex w-full flex-col gap-5 py-6">
    <header>
      <h2 class="mb-1 text-xl font-semibold text-n-slate-12">
        {{ copy.title }}
      </h2>
      <p class="mb-0 text-sm text-n-slate-10">
        {{ copy.subtitle }}
      </p>
    </header>

    <section class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
      <div class="flex flex-wrap items-end gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium text-n-slate-11">
            {{ copy.period }}
          </label>
          <WootDatePicker
            v-model:date-range="dateRange"
            v-model:range-type="rangeType"
            @date-range-changed="onDateRangeChanged"
          />
        </div>

        <Button
          :label="copy.filter"
          icon="i-lucide-list-filter"
          @click="applyFilters"
        />
        <Button
          :label="copy.clear"
          icon="i-lucide-rotate-ccw"
          color="slate"
          variant="outline"
          @click="clearFilters"
        />
      </div>
    </section>

    <div v-if="isLoading" class="flex items-center justify-center py-14">
      <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-10" />
    </div>

    <template v-else>
      <section class="rounded-lg border border-n-weak bg-n-solid-1 p-5">
        <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div class="rounded-lg bg-n-alpha-2 p-4">
            <span class="text-xs font-semibold uppercase text-n-slate-10">
              {{ copy.tokensUsed }}
            </span>
            <strong class="mt-4 block text-2xl text-n-slate-12">
              {{ formatNumber(summary.total_tokens) }}
            </strong>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-4">
            <span class="text-xs font-semibold uppercase text-n-slate-10">
              {{ copy.monthlyLimit }}
            </span>
            <strong class="mt-4 block text-2xl text-n-slate-12">
              {{ formatNumber(summary.monthly_limit) }}
            </strong>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-4">
            <span class="text-xs font-semibold uppercase text-n-slate-10">
              {{ copy.calls }}
            </span>
            <strong class="mt-4 block text-2xl text-n-slate-12">
              {{ formatNumber(summary.calls_count) }}
            </strong>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-4">
            <span class="text-xs font-semibold uppercase text-n-slate-10">
              {{ copy.period }}
            </span>
            <strong class="mt-4 block text-lg text-n-slate-12">
              {{ formatPeriod() }}
            </strong>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-4">
            <span class="text-xs font-semibold uppercase text-n-slate-10">
              {{ copy.openAiBalance }}
            </span>
            <template v-if="openAiBilling.status === 'available'">
              <strong class="mt-4 block text-2xl text-n-slate-12">
                {{
                  formatCurrency(
                    openAiBilling.remaining,
                    openAiBilling.currency
                  )
                }}
              </strong>
              <p class="mb-0 mt-1 text-xs text-n-slate-10">
                {{
                  formatCurrency(
                    openAiBilling.month_cost,
                    openAiBilling.currency
                  )
                }}
                {{ copy.openAiSpent }}
                {{
                  formatCurrency(
                    openAiBilling.monthly_limit,
                    openAiBilling.currency
                  )
                }}
              </p>
            </template>
            <p v-else class="mb-0 mt-4 text-sm leading-5 text-n-slate-11">
              {{ openAiBillingMessage }}
            </p>
          </div>
        </div>
        <div class="mt-4 h-2 overflow-hidden rounded-full bg-n-alpha-2">
          <div
            class="h-full rounded-full bg-n-brand"
            :style="{ width: `${usagePercent}%` }"
          />
        </div>
      </section>

      <section
        v-if="byFeature.length"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-5"
      >
        <div class="mb-4">
          <p class="mb-1 text-base font-semibold text-n-slate-12">
            {{ copy.byFeature }}
          </p>
          <p class="mb-0 text-sm text-n-slate-10">
            {{ copy.byFeatureDescription }}
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="item in featureItems"
            :key="item.feature_name"
            class="flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-colors hover:border-n-strong"
            :title="item.feature_name"
          >
            <div class="flex min-w-0 items-start gap-3">
              <div
                class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
              >
                <span
                  class="size-5"
                  :class="featurePresentation(item.feature_name).icon"
                />
              </div>
              <div class="min-w-0">
                <p class="mb-0 truncate text-sm font-semibold text-n-slate-12">
                  {{ featurePresentation(item.feature_name).label }}
                </p>
                <p class="mb-0 mt-0.5 line-clamp-2 text-xs text-n-slate-10">
                  {{ featurePresentation(item.feature_name).description }}
                </p>
              </div>
            </div>
            <div>
              <div class="mb-2 flex items-end justify-between gap-3">
                <span class="text-xs text-n-slate-10">
                  {{ formatNumber(item.calls_count) }} {{ copy.callsSuffix }}
                </span>
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ formatNumber(item.total_tokens) }} {{ copy.tokensSuffix }}
                </span>
              </div>
              <div class="h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
                <div
                  class="h-full rounded-full bg-n-brand"
                  :style="{ width: `${featureUsagePercent(item)}%` }"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="flex items-center justify-between gap-3 border-b border-n-weak px-5 py-3"
        >
          <p class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ copy.logs }}
          </p>
          <p class="mb-0 text-xs text-n-slate-10">
            {{ formatNumber(totalEvents) }} {{ copy.records }}
          </p>
        </div>

        <div v-if="!events.length" class="px-5 py-10 text-center">
          <p class="mb-0 text-sm text-n-slate-10">
            {{ copy.empty }}
          </p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full min-w-[1080px] table-fixed text-left text-sm">
            <thead class="border-b border-n-weak bg-n-alpha-1 text-n-slate-10">
              <tr>
                <th class="w-40 px-5 py-3 font-semibold">
                  {{ copy.columns.time }}
                </th>
                <th class="w-28 px-4 py-3 font-semibold">
                  {{ copy.columns.conversation }}
                </th>
                <th class="w-[28%] px-4 py-3 font-semibold">
                  {{ copy.columns.feature }}
                </th>
                <th class="w-44 px-4 py-3 font-semibold">
                  {{ copy.columns.model }}
                </th>
                <th class="w-72 px-4 py-3 font-semibold">
                  {{ copy.columns.tokens }}
                </th>
                <th class="w-28 px-4 py-3 text-right font-semibold">
                  {{ copy.columns.details }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="event in events"
                :key="event.id"
                class="transition-colors hover:bg-n-alpha-1"
              >
                <td class="whitespace-nowrap px-5 py-4 text-n-slate-11">
                  {{ formatDate(event.occurred_at) }}
                </td>
                <td class="whitespace-nowrap px-4 py-4">
                  <router-link
                    v-if="conversationRoute(event)"
                    :to="conversationRoute(event)"
                    class="font-medium text-n-brand hover:underline"
                  >
                    {{ copy.hash }}{{ event.conversation_display_id }}
                  </router-link>
                  <span v-else class="text-n-slate-10">
                    {{ copy.emptyValue }}
                  </span>
                </td>
                <td class="px-4 py-4">
                  <div class="flex min-w-0 items-center gap-3">
                    <div
                      class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
                    >
                      <span
                        class="size-4"
                        :class="featurePresentation(event.feature_name).icon"
                      />
                    </div>
                    <div class="min-w-0">
                      <p
                        class="mb-0 truncate font-medium text-n-slate-12"
                        :title="event.feature_name"
                      >
                        {{ featurePresentation(event.feature_name).label }}
                      </p>
                      <p
                        class="mb-0 mt-0.5 truncate text-xs text-n-slate-10"
                        :title="event.source_type"
                      >
                        {{ sourceLabel(event.source_type) }}
                      </p>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-4 text-n-slate-11">
                  <span
                    class="inline-flex max-w-full truncate rounded-md bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-11"
                    :title="event.model || event.provider"
                  >
                    {{ formatModel(event.model || event.provider) }}
                  </span>
                </td>
                <td class="px-4 py-4">
                  <div class="grid grid-cols-3 gap-2">
                    <div>
                      <span class="block text-[11px] text-n-slate-10">
                        {{ copy.tokenInput }}
                      </span>
                      <span class="font-medium text-n-slate-11">
                        {{ formatNumber(event.input_tokens) }}
                      </span>
                    </div>
                    <div>
                      <span class="block text-[11px] text-n-slate-10">
                        {{ copy.tokenOutput }}
                      </span>
                      <span class="font-medium text-n-slate-11">
                        {{ formatNumber(event.output_tokens) }}
                      </span>
                    </div>
                    <div class="border-l border-n-weak pl-3">
                      <span class="block text-[11px] text-n-slate-10">
                        {{ copy.tokenTotal }}
                      </span>
                      <span class="font-semibold text-n-slate-12">
                        {{ formatNumber(event.total_tokens) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-4 text-right text-n-slate-11">
                  <details v-if="metadataText(event.metadata)" class="max-w-xs">
                    <summary
                      class="inline-flex cursor-pointer list-none items-center gap-1 whitespace-nowrap rounded-lg px-2 py-1.5 font-medium text-n-brand hover:bg-n-brand/10"
                    >
                      <span class="i-lucide-file-search size-4" />
                      {{ copy.seeLog }}
                    </summary>
                    <code
                      class="mt-2 block max-h-40 overflow-auto whitespace-pre rounded bg-n-alpha-2 p-3 text-left text-xs text-n-slate-11"
                    >
                      {{ metadataText(event.metadata) }}
                    </code>
                  </details>
                  <span v-else>{{ copy.emptyValue }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between border-t border-n-weak px-5 py-3"
        >
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak px-3 text-sm text-n-slate-11 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="page <= 1"
            @click="page -= 1"
          >
            {{ copy.previous }}
          </button>
          <span class="text-xs text-n-slate-10">
            {{ copy.page }} {{ page }} {{ copy.of }} {{ totalPages }}
          </span>
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak px-3 text-sm text-n-slate-11 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="page >= totalPages"
            @click="page += 1"
          >
            {{ copy.next }}
          </button>
        </div>
      </section>
    </template>
  </div>
</template>
